import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  const { login, resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      setSuccess('');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);
      const result = await login(email, password);

      if (result.success) {
        setSuccess('Login successful! Redirecting to dashboard...');
      } else {
        setError(result.error || 'Failed to log in');
      }
    } catch (err) {
      setError('Failed to log in: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }

    try {
      setResetError('');
      setLoading(true);
      const result = await resetPassword(resetEmail);

      if (result.success) {
        setResetSuccess(true);
      } else {
        setResetError(result.error || 'Failed to send password reset email');
      }
    } catch (err) {
      setResetError('Failed to send password reset email: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{
      background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="card shadow-lg border-0" style={{ borderRadius: '24px' }}>
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <i className="bi bi-shop-window" style={{ fontSize: '4rem', color: '#2196F3' }}></i>
                  </div>
                  <h2 className="gradient-text fw-bold">POS Admin Portal</h2>
                  <p className="text-muted">Sign in with your merchant ID and password or admin email and password</p>
                </div>

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                  </div>
                )}

                {success && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {success}
                    <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Merchant ID or Email</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 mb-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : 'Sign In'}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-link text-primary"
                      onClick={() => setShowResetModal(true)}
                    >
                      Forgot password?
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="text-center mt-3">
              <p className="text-white">© 2025 POS Admin. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowResetModal(false)}></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reset Password</h5>
                  <button type="button" className="btn-close" onClick={() => setShowResetModal(false)}></button>
                </div>
                <div className="modal-body">
                  {resetSuccess ? (
                    <div className="alert alert-success">
                      Password reset email sent! Please check your inbox.
                    </div>
                  ) : (
                    <>
                      {resetError && (
                        <div className="alert alert-danger">
                          {resetError}
                        </div>
                      )}
                      <p>Enter your email address and we'll send you a link to reset your password.</p>
                      <div className="mb-3">
                        <label htmlFor="resetEmail" className="form-label">Email Address</label>
                        <input
                          type="email"
                          className="form-control"
                          id="resetEmail"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowResetModal(false)}>
                    Close
                  </button>
                  {!resetSuccess && (
                    <button type="button" className="btn btn-primary" onClick={handleResetPassword} disabled={loading}>
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
